import React, { useEffect, useRef, useState, useMemo } from "react";
import Select, { components } from 'react-select';
import TreeList, { MenuType } from "./../Widgets/TreeList";
import styles from "./TreeSelect.module.scss";
import _ from "lodash";
import classNames from "classnames";
import { Button, Span } from "./../../";
import { CloseIcon, SearchIcon } from "../../../icons/monochrome";

const { SingleValue, MultiValue, ClearIndicator } = components;

// Default Filtering is done on the top level of Tree view and compared only with labelKey,
// Consumers can pass this filtering according to their needs.
const DefaultFiltering = (data, filterKey, searchKey) => {
    if (!_.isUndefined(filterKey) && filterKey !== '') {
        const lowerCasedFilter = _.toLower(filterKey);
        return _.filter(data, option => {
            return (option[searchKey] && _.includes(_.toLower(option[searchKey]), lowerCasedFilter))
        })
    } else {
        return data;
    }
}

const getDefaultSelectedValues = (treeData, defaultSelectedValues, uniqueIdentifier, childKey) => {
    let defaultValues = [];
    if (defaultSelectedValues) {
        treeData.map((treeItem) => {
            if (_.findIndex(defaultSelectedValues, (selectedValue) => _.isEqual(selectedValue[uniqueIdentifier], treeItem[uniqueIdentifier])) > -1) {
                defaultValues.push(treeItem);
            }
            if (!(_.isUndefined(treeItem[childKey]) || _.isEmpty(treeItem[childKey]))) {
                defaultValues.push(...getDefaultSelectedValues(treeItem[childKey], defaultSelectedValues, uniqueIdentifier, childKey));
            }
        });
    }
    return defaultValues
}

// Styles to be set for Dropdown Menu, Single/Multi value Rendering.
const contentTypeSelectorStyles = {
    singleValue: () => ({
        fontSize: '14px',
        color: 'var(--text-primary)',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        padding: '0px 15px'
    }),
    multiValue: (base) => ({
        ...base,
        border: `2px solid var(--button-primary-bg)`,
        backgroundColor: 'var(--button-primary-bg)',
        color: 'white',
        padding: '0 5px',
        margin: '0 2px'
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: 'white'
    }),
    multiValueRemove: () => ({
        display: 'none'
    }),
    indicatorSeparator: () => ({
        display: 'none'
    }),
    valueContainer: (base) => ({
        ...base,
        padding: 0
    }),
    control: (base) => ({
        ...base,
        minHeight: '40px',
        padding: '0px 5px',
    }),
    placeholder: (base) => ({
        ...base,
        color: 'var(--text-disabled)',
        fontSize: '14px',
        padding: '0 10px',
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999999999
    })
};

// Custom Menu list dropdown down to render the tree grid along with search input field and accept/cancel button
const CustomMenuList = (menuListProps) => {
    const { selectProps,
        setValue } = menuListProps;
    const { onInputChange,
        inputValue,
        onMenuInputFocus,
        treeListParams } = selectProps;
    const { treeData,
        labelKey,
        isSearchable,
        childKey,
        menuType,
        defaultSelectedValues,
        selectedValues,
        dropdownUpIcon,
        dropdownDownIcon,
        isOptionDisabled,
        onSelectionChange,
        isRequireAcceptOnChange,
        acceptChanges,
        searchPlaceholder,
        isFirst,
        customFilter,
        noOptionsMessage,
        minMenuHeight,
        maxMenuHeight,
        uniqueIdentifier,
        iconRenderer,
        expandAll,
        bottomBorder
     } = treeListParams;

    // This state is to manage the final selected values. In case of multi selection, if user selects
    // multiple choices and clicks on cancel, it should updated the last accepted value. 
    const [lastSelectedValues, setLastSelectedValues] = useState([]);

    // The filter value user had searched for
    const [filter, setFilter] = useState();
    // Filtering function
    const filteredOptions = useMemo(() => customFilter(treeData, filter, labelKey), [filter]);

    const ariaAttributes = {
        "aria-autocomplete": "list",
        "aria-label": selectProps["aria-label"],
        "aria-labelledby": selectProps["aria-labelledby"]
    };

    // This method is called with accept or cancel button is clicked.
    const onAcceptOrCancel = (isAccept) => {
        setValue(isAccept ? [...lastSelectedValues] : [...selectedValues]);
        onSelectionChange(isAccept ? [...lastSelectedValues] : [...selectedValues]);
        acceptChanges();
    }
    // This method is called when any change in the items slection.
    const onOptionSelected = (selectedValues) => {
        setLastSelectedValues([...selectedValues]);
        if (!(isRequireAcceptOnChange) || (isFirst && defaultSelectedValues)) {
            setValue([...selectedValues]);
            onSelectionChange([...selectedValues]);
        }
    }
    // Search Widget is not used because the search input field is becoming non clickable inside the select dropdown due to auto focus option
    // So an input field is customized to show inside the dropdown menu.
    // Search Widget works fine outside of the dropdown menu.
    return (<>
        {isSearchable && <div className={classNames(styles.base)}>
            <div className={classNames(styles.widget, styles.open)} >
                <Button variant="icon" className={classNames(styles['open-button'], styles['always-open'])} tabIndex={-1}><SearchIcon /></Button>
                {/* This is Search Input Field, placeholder is configurable */}
                <input
                    className={classNames(styles.inputField)}
                    autoCorrect="off"
                    autoComplete="off"
                    spellCheck="false"
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                        setFilter(e.currentTarget.value);
                        onInputChange(e.currentTarget.value, {
                            action: "input-change"
                        })
                    }
                    }
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        e.target.focus();
                    }}
                    onTouchEnd={(e) => {
                        e.stopPropagation();
                        e.target.focus();
                    }}
                    onFocus={onMenuInputFocus}
                    placeholder={searchPlaceholder}
                    {...ariaAttributes}
                />
                <button type="button" className={classNames(styles['close-button'], { [styles["show-close-button"]]: filter?.length > 0 })} onClick={(e) => {
                    setFilter('');
                    onInputChange('', {
                        action: "input-change"
                    })
                }} aria-label="Clear search"><CloseIcon /></button>
            </div>
        </div>}
        {filteredOptions && filteredOptions?.length > 0 && <TreeList treeData={filteredOptions}
            childKey={childKey}
            menuType={menuType}
            selectedValues={selectedValues ? [...selectedValues] : []}
            onChange={onOptionSelected}
            dropdownUpIcon={dropdownUpIcon}
            dropdownDownIcon={dropdownDownIcon}
            isOptionDisabled={isOptionDisabled}
            labelKey={labelKey}
            minHeight={minMenuHeight}
            maxHeight={maxMenuHeight}
            uniqueIdentifier={uniqueIdentifier}
            iconRenderer={iconRenderer}
            expandAll={expandAll}
            bottomBorder={bottomBorder}
        />}
        {!filteredOptions?.length && <div className={styles["no-options"]}>
            <Span bold>{noOptionsMessage ? noOptionsMessage() : "No Options"}</Span>
        </div>}
        {isRequireAcceptOnChange && (filteredOptions?.length > 0) && <div className={styles["menu-actions"]}>
            <button onClick={() => {
                onAcceptOrCancel(true);
            }}>Accept</button>
            <button onClick={() => onAcceptOrCancel(false)}>Cancel</button>
        </div>}
    </>
    );
};

const CustomSingleValue = ({ children, selectProps, ...props }) => {
    const { treeListParams } = selectProps;
    const { labelKey } = treeListParams;

    return (
        <SingleValue {...props}>{_.get(props.getValue()[0], labelKey)}</SingleValue>
    )
}

const CustomMultiValue = ({ children, selectProps, ...props }) => {
    const { treeListParams } = selectProps;
    const { labelKey } = treeListParams;
    const { index } = props;

    return (
        <MultiValue {...props}>{_.get(props.getValue()[index], labelKey)}</MultiValue>
    )
}

// This is customized component for close icon inside select input field
const CustomClearIndicator = (props) => {
    const { clearValue, selectProps, innerProps } = props;
    const { treeListParams } = selectProps;
    const { onSelectionChange } = treeListParams;
    // This method will be called to clear all the selected elements at once in case of multiple selection
    const clearAll = () => {
        onSelectionChange([]);
        clearValue();
    }

    const customInnerProps = {
        ...innerProps,
        onMouseDown: clearAll,
        onTouchEnd: clearAll
    };
    return (
        <ClearIndicator {...props} innerProps={customInnerProps}></ClearIndicator>
    );
};

const TreeSelect = React.memo(({
    treeData,
    childKey = 'childrens',
    labelKey = 'label',
    dropdownUpIcon,
    dropdownDownIcon,
    menuType = MenuType.SINGLE,
    defaultSelectedValues = [],
    isOptionDisabled,
    onChange,
    customSelectorStyles,
    inlineSelect = false,
    isSearchable = false,
    isRequireAcceptOnChange = false,
    searchPlaceholder = 'Search...',
    selectPlaceholder = "Select...",
    customFilter = DefaultFiltering,
    noOptionsMessage,
    minMenuHeight = 50,
    maxMenuHeight = 250,
    menuPortalTarget,
    isSelectDisabled,
    uniqueIdentifier,
    iconRenderer,
    name,
    expandAll,
    className,
    bottomBorder
}) => {
    const isMulti = _.toLower(menuType) === _.toLower(MenuType.MULTI);
    const containerRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [isFirst, setIsFirst] = useState(true);
    const [selectedValues, setSelectedValues] = useState(getDefaultSelectedValues(treeData, defaultSelectedValues, uniqueIdentifier, childKey));

    // method called with selection changes in child component.
    const onSelectionChange = (selectedValues) => {
        setSelectedValues([...selectedValues]);
    }

    // useEffect hook to pass the selected values to parent component when selectedValues changes.
    useEffect(() => {
        if (!isFirst) {
            onChange([...selectedValues], {name});
        }
        if (!(isRequireAcceptOnChange)) {
            if (!isFirst) setIsFocused(false);
        }
        setIsFirst(false)
    }, [selectedValues])

    // Change the focus to select field when Items are selected.
    const acceptChanges = () => {
        setIsFocused(false);
    }

    // These are styles are to customize the select component
    let selectStyles = {
        ...contentTypeSelectorStyles,
    }

    // If it is inline set few css properties to container and control to make it look inline.
    if (inlineSelect) {
        selectStyles = {
            ...selectStyles,
            container: (provided) => ({
                ...provided,
                borderBottom: '1px solid var(--field-border)'
            }),
            control: (provided) => ({
                ...provided,
                cursor: 'pointer',
                backgroundColor: 'unset',
                border: '0',
                boxShadow: 'unset',
                cursor: 'pointer',
                height: '40px'
            }),
        }
    }

    if (isSearchable) {
        maxMenuHeight = maxMenuHeight - 40;
    }
    if (isRequireAcceptOnChange) {
        maxMenuHeight = maxMenuHeight - 40;
    }

    return (
        <div className={classNames(className)} ref={containerRef}>
            <Select
                defaultValue={[...getDefaultSelectedValues(treeData, defaultSelectedValues, uniqueIdentifier, childKey)]}
                styles={{ ...selectStyles, ...customSelectorStyles }}
                classNamePrefix="select"
                components={{
                    MenuList: CustomMenuList,
                    SingleValue: CustomSingleValue,
                    MultiValue: CustomMultiValue,
                    ClearIndicator: CustomClearIndicator
                }}
                inputValue={inputValue}
                isSearchable={false}
                isMulti={isMulti}
                isDisabled={isSelectDisabled}
                onMenuInputFocus={() => {
                    setIsFocused(true)
                }}
                menuPortalTarget={menuPortalTarget ? menuPortalTarget : null}
                onChange={() => {
                    setIsFocused(isFirst ? true : isMulti);
                }}
                onInputChange={(val) => { setInputValue(val) }}
                menuIsOpen={isFocused}
                onMenuOpen={() => {
                    setIsFirst(true);
                    setIsFocused(true);
                }}
                onMenuClose={() => setIsFocused(false)}
                isFocused={isFocused}
                openMenuOnClick={true}
                placeholder={selectPlaceholder}
                menuPlacement={'auto'}
                backspaceRemovesValue={false}
                treeListParams={
                    {
                        treeData,
                        childKey,
                        labelKey,
                        dropdownUpIcon,
                        dropdownDownIcon,
                        menuType,
                        selectedValues,
                        defaultSelectedValues,
                        isOptionDisabled,
                        onSelectionChange,
                        customSelectorStyles,
                        inlineSelect,
                        isSearchable,
                        isRequireAcceptOnChange,
                        acceptChanges,
                        searchPlaceholder,
                        isFirst,
                        customFilter,
                        noOptionsMessage,
                        minMenuHeight,
                        maxMenuHeight,
                        uniqueIdentifier,
                        iconRenderer,
                        expandAll,
                        bottomBorder
                    }
                }
            />
        </div>
    );
})

export default TreeSelect
